import React, {useEffect, useId, useState} from "react";
import {motion, AnimatePresence} from "framer-motion";

const LEVEL_STATUS = {
    NOT_ATTEMPTED: "notAttempted",
    ATTEMPTED: "attempted",
    ACHIEVED: "achieved",
}
const STAR_ICON = "M20.5,9.72c-.07-.18-.17-.33-.3-.45-.13-.12-.32-.19-.55-.23l-4.85-.42-1.88-4.45c-.08-.2-.21-.35-.39-.45-.17-.1-.35-.15-.54-.15s-.36.05-.54.15c-.18.1-.3.25-.39.45l-1.88,4.45-4.85.42c-.23.03-.42.11-.55.23-.13.12-.23.27-.3.45-.07.18-.08.37-.04.56.04.19.15.36.31.51l3.67,3.17-1.1,4.73c-.05.22-.03.41.05.59.08.18.2.32.35.44.15.12.33.18.53.2.2.02.39-.03.57-.15l4.15-2.5,4.15,2.5c.18.12.38.17.57.15.2-.02.38-.08.53-.2.15-.12.27-.26.35-.44.08-.17.1-.37.05-.59l-1.1-4.73,3.67-3.17c.17-.15.27-.32.31-.51.04-.19.03-.38-.04-.56ZM12,12h0Z";

const LevelMarker = props => {
    const {attempted = false, achieved = false} = props;
    const [secondaryColor, setSecondaryColor] = useState("green");

    const uniqueId = useId().replace(/:/g, "");
    const maskId = `mask-${uniqueId}`;
    const gradientId = `grad-${uniqueId}`;
    const shadowGradId = `shadow-${uniqueId}`;
    const glossId = `gloss-${uniqueId}`;
    const status = achieved
        ? LEVEL_STATUS.ACHIEVED
        : attempted
            ? LEVEL_STATUS.ATTEMPTED
            : LEVEL_STATUS.NOT_ATTEMPTED;
    const statusStyles = {
        stroke: "#fff",
        strokeWidth: 1,
        notAttempted: {
            fill: "rgba(255, 255, 255, 0.4)",
        },
        attempted: {
            fill: secondaryColor,
        },
        achieved: {
            fill: "#ffc107",
        }
    }

    useEffect(() => {
        const color = getComputedStyle(document.documentElement)
            .getPropertyValue('--secondary-colour').trim();
        if (color) setSecondaryColor(color);
    }, []);

    return (
        <svg viewBox='0 0 24 24' width="40" height="40" style={{overflow: "visible"}}>
            <AnimatePresence mode="wait" initial={false}>
                {status === LEVEL_STATUS.NOT_ATTEMPTED && (
                    <path
                        d={STAR_ICON}
                        fill={statusStyles.notAttempted.fill}
                        stroke={statusStyles.stroke}
                        strokeWidth={statusStyles.strokeWidth}
                        strokeLinejoin="round"
                    />
                )}
                {status === LEVEL_STATUS.ATTEMPTED && (
                    <motion.g
                        key="status-attempted"
                        initial={{scale: 1, opacity: 1}}
                        animate={{
                            scale: [1, 1.1, 1],
                            opacity: 1
                        }}
                        exit={{scale: 0.5, opacity: 0, transition: {duration: 0.2}}}
                        transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 20,
                            scale: {duration: 0.2, ease: "easeInOut"}
                        }}
                    >
                        <defs>
                            <clipPath id="star-boundary">
                                <path d={STAR_ICON} />
                            </clipPath>
                            <linearGradient id={shadowGradId} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={statusStyles.attempted.fill} stopOpacity="0" />
                                <stop offset="100%" stopColor="black" stopOpacity="0.3" />
                            </linearGradient>
                            <mask id={maskId}>
                                <rect x="0" y="0" width="24" height="24" fill="black" />
                                <motion.rect
                                    x="0"
                                    width="24"
                                    height="24"
                                    fill="#fff"
                                    rx="1"
                                    initial={{y: 24}}
                                    animate={{y: 12}}
                                    transition={{duration: 1.2, ease: [0.33, 1, 0.68, 1], delay: 0.2}}
                                />
                            </mask>
                        </defs>
                        <motion.path
                            d={STAR_ICON}
                            fill={statusStyles.notAttempted.fill}
                            stroke={statusStyles.stroke}
                            strokeLinejoin="round"
                            initial={{strokeWidth: statusStyles.strokeWidth}}
                            animate={{strokeWidth: statusStyles.strokeWidth + 0.5}}
                            transition={{duration: .4, ease: "easeInOut"}}
                        />
                        <path
                            d={STAR_ICON}
                            fill={statusStyles.attempted.fill}
                            mask={`url(#${maskId})`}
                        />
                        <path
                            d={STAR_ICON}
                            fill={`url(#${shadowGradId})`}
                            mask={`url(#${maskId})`}
                            style={{ pointerEvents: 'none' }}
                        />
                        <g mask={`url(#${maskId})`}>
                            <StarHighlights glossId={glossId} />
                        </g>
                        <motion.path
                            d={STAR_ICON}
                            fill="none"
                            stroke={statusStyles.attempted.fill}
                            strokeWidth={statusStyles.strokeWidth * 2}
                            strokeLinejoin="round"
                            clipPath="url(#star-boundary)"
                            initial={{pathLength: 0}}
                            animate={{pathLength: 1}}
                            transition={{duration: 1, ease: "easeInOut"}}
                        />
                    </motion.g>
                )}
                {status === LEVEL_STATUS.ACHIEVED && (
                    <motion.g key="achieved">
                        <defs>
                            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#FFD700" />
                                <stop offset="100%" stopColor="#FFA000" />
                            </linearGradient>
                            <clipPath id="star-reflection-clip">
                                <path d={STAR_ICON} />
                            </clipPath>
                        </defs>
                        <Shockwave baseColor={statusStyles.achieved.fill} />
                        <CelebrationSprites baseColor={statusStyles.achieved.fill} />
                        <motion.g
                            initial={{scale: 0, opacity: 0}}
                            animate={{scale: [0, 1.5, 1.5, 1], opacity: 1}}
                            transition={{
                                scale: {
                                    type: "spring",
                                    damping: 14,
                                    stiffness: 220,
                                    duration: 0.9,
                                    times: [0, 0.35, 0.75, 1]
                                },
                                opacity: {duration: 0.2}
                            }}
                        >
                            <path
                                d={STAR_ICON}
                                fill={`url(#${gradientId})`}
                                stroke={statusStyles.stroke}
                                strokeWidth={statusStyles.strokeWidth}
                                strokeLinejoin="round"
                                // style={{filter: "drop-shadow(0px 2px 3px rgba(0,0,0,0.3))"}}
                            />
                            <StarHighlights glossId={glossId} />
                        </motion.g>
                    </motion.g>
                )}
            </AnimatePresence>
        </svg>
    );
}

const StarHighlights = props => {
    const {glossId} = props;

    return (
        <g style={{pointerEvents: 'none'}}>
            <defs>
                <radialGradient id={glossId} cx="50%" cy="30%" r="50%">
                    <stop offset="0%" stopColor="white" stopOpacity="0.5"/>
                    <stop offset="100%" stopColor="white" stopOpacity="0"/>
                </radialGradient>
            </defs>
            <path d={STAR_ICON} fill={`url(#${glossId})`} />
            <circle fill="#fff" fillOpacity="0.3" cx="5.44" cy="10.37" r=".4"/>
            <path fill="#fff" fillOpacity="0.3" d="M11.33,10.2c-.08.39-.29.69-.67.7l-1.92-.03c-.33.01-.61-.25-.62-.58-.01-.33.25-.61.58-.62l1.19-.1,1.15-2.74s0,0,0,0h0s0,0,0,0c.08-.17.26-.29.46-.28.28,0,.49.24.49.51,0,.06-.02.11-.04.16l-.63,3Z"/>
            <path fill="#000" fillOpacity="0.05" d="M15.07,12.42c-.25.2-.29.55-.09.8s.55.29.8.09l1.94-1.67c.21-.17.24-.47.08-.68s-.47-.24-.68-.08l-2.04,1.54Z"/>
            <path fill="#000" fillOpacity="0.05" d="M10.94,16.73c.23-.14.29-.44.15-.67-.14-.23-.44-.29-.67-.15l-1.93,1.31c-.19.12-.25.38-.13.57.12.19.38.25.57.13l2-1.2Z"/>
        </g>
    );
}

const Shockwave = props => {
    const {baseColor = "#ffc107"} = props;

    return (
        <g>
            <motion.circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke={baseColor}
                initial={{scale: 0, opacity: 0, strokeWidth: 0}}
                animate={{
                    scale: [0, 1.2, 1.8],
                    opacity: [0, 1, 0.8, 0],
                    strokeWidth: [8, 4, 0.5, 0],
                }}
                transition={{
                    duration: 0.7,
                    ease: [0.16, 1, 0.3, 1],
                    times: [0, 0.2, 0.8, 1],
                }}
            />
            <motion.circle
                cx="12"
                cy="12"
                r="10"
                fill="none"
                stroke={baseColor}
                initial={{scale: 0, opacity: 0, strokeWidth: 0}}
                animate={{
                    scale: [0, 1.5],
                    opacity: [0, 0.5, 0],
                    strokeWidth: [4, 0],
                }}
                transition={{
                    duration: 0.5,
                    ease: "easeOut",
                    times: [0, 0.2, 1],
                }}
            />
        </g>
    );
};

const CelebrationSprites = props => {
    const {baseColor = "#ffc107"} = props;
    const sparkles = React.useMemo(() => {
        const spriteCountMax = 10;
        const spriteCountMin = 6;
        const totalSprites = Math.floor(Math.random() * (spriteCountMax - spriteCountMin + 1) + spriteCountMin);

        const arr = [];
        for (let i = 0; i < totalSprites; i++) {
            arr.push({
                angle: Math.random() * 360,
                brightness: 80 + Math.random() * 40,
                hueShift: Math.floor(Math.random() * 40 - 20),
                randomScale: Math.random() * (1.8 - 0.6) + 0.6,
                delay: Math.random() * 0.2,
            });
        }
        return arr;
    }, [baseColor]);

    return (
        <g>
            {sparkles.map((sprite, i) => (
                <motion.rect
                    key={i}
                    x="11"
                    y="11"
                    width="2"
                    height="2"
                    rx="1"
                    fill={baseColor}
                    style={{
                        filter: `hue-rotate(${sprite.hueShift}deg) brightness(${sprite.brightness}%)`
                    }}
                    initial={{
                        scale: 0,
                        rotate: 45,
                        x: 0,
                        y: 0,
                        opacity: 1
                    }}
                    animate={{
                        scale: [0, sprite.randomScale, 0],
                        opacity: [1, 1, 0],
                        x: Math.cos(sprite.angle * Math.PI / 180) * 14,
                        y: Math.sin(sprite.angle * Math.PI / 180) * 14,
                        rotate: [45, 225],
                    }}
                    transition={{
                        duration: 0.9,
                        ease: [0.23, 1, 0.32, 1]
                    }}
                />
            ))}
        </g>
    );
};

export default LevelMarker;

export const LevelMarkerTest = () => {
    const [step, setStep] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setStep((prev) => (prev + 1) % 3);
        }, 4000);

        return () => clearInterval(interval);
    }, []);

    const lmProps = {
        attempted: step === 1 || step === 2,
        achieved: step === 2,
    };

    return (
        <LevelMarker {...lmProps} />
    )
}